
/** Max rows per export to avoid browser memory issues */
export const MAX_EXPORT_ROWS = 50000;

export const DEFAULT_PAGE_SIZE = 500;

/**
 * Paginate through list API until all rows for current filters are loaded.
 * @param {object} options
 * @param {(opts: { limit: number, offset: number }) => Promise<{ data: array, count: number }>} options.fetchPage
 * @param {number} [options.pageSize]
 * @param {number} [options.maxRows]
 * @returns {Promise<{ rows: array, total: number, truncated: boolean }>}
 */
export async function fetchAllFilteredRows({ fetchPage, pageSize = DEFAULT_PAGE_SIZE, maxRows = MAX_EXPORT_ROWS }) {
  const first = await fetchPage({ limit: pageSize, offset: 0 });
  const total = typeof first.count === "number" ? first.count : (first.data?.length ?? 0);
  if (total === 0) {
    return { rows: [], total: 0, truncated: false };
  }
  if (total > maxRows) {
    const err = new Error("EXPORT_TOO_LARGE");
    err.total = total;
    err.maxRows = maxRows;
    throw err;
  }

  let rows = [...(first.data ?? [])];
  while (rows.length < total) {
    const next = await fetchPage({ limit: pageSize, offset: rows.length });
    const batch = next.data ?? [];
    if (batch.length === 0) break;
    rows = rows.concat(batch);
    if (batch.length < pageSize) break;
  }
  if (rows.length > total) rows = rows.slice(0, total);
  return { rows, total, truncated: rows.length < total };
}

/**
 * @param {object} options
 * @param {string} options.filename - e.g. ad-requests-2026-03-31.xlsx
 * @param {string} [options.sheetName]
 * @param {{ header: string, value: (row: object) => unknown, link?: (row: object) => string }[]} options.columns
 * @param {object[]} options.rows
 */
// Loaded on demand: the styled writer is ~200kB gzipped, so it stays out of the main bundle.
export async function downloadExcelFromRows({ filename, sheetName = "Sheet1", columns, rows }) {
  const XLSX = await import("xlsx-js-style");
  const headers = columns.map((c) => c.header);
  const dataRows = rows.map((row) =>
    columns.map((c) => {
      const v = c.value(row);
      if (v === null || v === undefined) return "";
      if (typeof v === "object") return JSON.stringify(v);
      return v;
    }),
  );
  const aoa = [headers, ...dataRows];
  const ws = XLSX.utils.aoa_to_sheet(aoa);

  // Columns with link() become hyperlinks; one target per cell, so a multi-link cell uses the first.
  columns.forEach((c, C) => {
    if (!c.link) return;
    rows.forEach((row, i) => {
      const target = c.link(row);
      if (!target) return;
      const cell = ws[XLSX.utils.encode_cell({ r: i + 1, c: C })];
      if (cell) cell.l = { Target: String(target), Tooltip: String(target) };
    });
  });

  // Apply bold + wrap to headers; wrap text on all data cells
  const range = XLSX.utils.decode_range(ws["!ref"]);
  for (let C = range.s.c; C <= range.e.c; C++) {
    const headerCell = ws[XLSX.utils.encode_cell({ r: 0, c: C })];
    if (headerCell) headerCell.s = { font: { bold: true }, alignment: { wrapText: true, vertical: "top" } };
  }
  for (let R = 1; R <= range.e.r; R++) {
    for (let C = range.s.c; C <= range.e.c; C++) {
      const cell = ws[XLSX.utils.encode_cell({ r: R, c: C })];
      if (cell) cell.s = { alignment: { wrapText: true, vertical: "top" } };
    }
  }

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName.slice(0, 31));
  XLSX.writeFile(wb, filename.endsWith(".xlsx") ? filename : `${filename}.xlsx`, { cellStyles: true });
}

export function exportFilename(prefix) {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${prefix}-${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}.xlsx`;
}
